/**
 * ClassesTable - Table component for displaying classes
 *
 * @example
 * ```tsx
 * <ClassesTable data={classes} />
 * ```
 */

"use client";

import {
  Box,
  Divider,
  Flex,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useRouter } from "next/navigation";
import type React from "react";
import { useCallback, useMemo } from "react";
import type {
  ClassDayResponse,
  GetClassResponse,
  GetClassRoomResponse,
  GetLocationResponse,
} from "@/openapi/generated";
import { formatTimeTo12Hour } from "@/lib/utils";
import { useThemeTokens } from "@/lib/theme/tokens";
import { Card, Text } from "@/components/ui";

const COL_WIDTHS = ["45%", "30%", "25%"] as const;
const columnHelper = createColumnHelper<GetClassResponse>();

interface ClassesTableProps {
  data: GetClassResponse[];
}

interface NameCellProps {
  name: string;
  description: string | null;
}

const NameCell = ({ name, description }: NameCellProps) => {
  const theme = useThemeTokens();
  return (
    <Flex direction="column">
      <Text variant="primary" fontSize="sm" fontWeight="700">
        {name}
      </Text>

      {description && (
        <Text variant="secondary" fontSize="sm" noOfLines={2}>
          {description}
        </Text>
      )}
    </Flex>
  );
};

interface ScheduleCellProps {
  days: ClassDayResponse[];
}

const ScheduleCell = ({ days }: ScheduleCellProps) => {
  const theme = useThemeTokens();
  if (!days?.length) {
    return <Text variant="secondary">-</Text>;
  }

  return (
    <Flex direction="column" rowGap="4px">
      {days.map((d) => (
        <Flex
          key={`${d.day}-${d.startTime}-${d.endTime}`}
          justifyContent="space-between"
        >
          <Text variant="primary" fontSize="sm" fontWeight="600">
            {d.day}:
          </Text>
          <Text variant="secondary" fontSize="sm" textAlign="right">
            {formatTimeTo12Hour(d.startTime)} – {formatTimeTo12Hour(d.endTime)}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

interface FacilityCellProps {
  location: GetLocationResponse | null;
  classRoom: GetClassRoomResponse | null;
}

const FacilityCell = ({ location, classRoom }: FacilityCellProps) => {
  const theme = useThemeTokens();
  const items = [
    classRoom?.name && { label: "Classroom", value: classRoom.name },
    location?.name && { label: "Location", value: location.name },
  ].filter(Boolean) as { label: string; value: string }[];

  if (!items.length) {
    return <Text variant="secondary">-</Text>;
  }

  return (
    <Flex direction="column" rowGap="4px">
      {items.map((item) => (
        <Flex key={item.label} justifyContent="space-between">
          <Text variant="primary" fontSize="sm" fontWeight="600">
            {item.label}:
          </Text>
          <Text variant="secondary" fontSize="sm" textAlign="right">
            {item.value}
          </Text>
        </Flex>
      ))}
    </Flex>
  );
};

export const ClassesTable: React.FC<ClassesTableProps> = ({ data }) => {
  const router = useRouter();
  const theme = useThemeTokens();

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        id: "name",
        header: () => (
          <Text variant="tertiary" fontSize={{ sm: "10px", lg: "12px" }}>
            CLASS
          </Text>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <NameCell
              name={row.name}
              description={row.description}
            />
          );
        },
      }),

      columnHelper.accessor("classDays", {
        id: "classDays",
        header: () => (
          <Text variant="tertiary" fontSize={{ sm: "10px", lg: "12px" }}>
            SCHEDULE
          </Text>
        ),
        cell: (info) => (
          <ScheduleCell days={info.row.original.classDays} />
        ),
      }),

      columnHelper.accessor("location", {
        id: "facility",
        header: () => (
          <Text variant="tertiary" fontSize={{ sm: "10px", lg: "12px" }}>
            FACILITY
          </Text>
        ),
        cell: (info) => {
          const row = info.row.original;
          return (
            <FacilityCell
              location={row.location}
              classRoom={row.classRoom}
            />
          );
        },
      }),
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const goToDetails = useCallback(
    (slug: string) => {
      if (!slug) return;
      router.push(`/classes/${slug}`);
    },
    [router],
  );

  return (
    <>
      <Box display={{ base: "none", md: "block" }} overflowX="auto">
        <Table
          mt="12px"
          minW="100%"
          color="gray.500"
          sx={{ tableLayout: "fixed" }}
        >
          <Thead>
            {table.getHeaderGroups().map((hg) => (
              <Tr key={hg.id}>
                {hg.headers.map((header, i) => (
                  <Th
                    pe="10px"
                    key={header.id}
                    w={COL_WIDTHS[i]}
                    borderColor={theme.borderGlass.default}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>

          <Tbody>
            {table.getRowModel().rows.map((row) => (
              <Tr
                key={String(row.original.classId)}
                onClick={() =>
                  row.original.slug && goToDetails(row.original.slug)
                }
                cursor="pointer"
                transition="all 0.2s ease"
                borderLeft="4px solid transparent"
                sx={{
                  "@media (hover: hover)": {
                    "&:hover": {
                      bg: theme.accent.primaryBg,
                      borderColor: theme.accent.primaryBorder,
                    },
                  },
                }}
              >
                {row.getVisibleCells().map((cell, i) => (
                  <Td
                    key={cell.id}
                    w={COL_WIDTHS[i]}
                    verticalAlign="top"
                    borderColor="transparent"
                    fontSize={{ sm: "14px" }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Flex
        mt={4}
        gap={4}
        direction="column"
        display={{ base: "flex", md: "none" }}
      >
        {data.map((item) => (
          <Card
            p={4}
            cursor="pointer"
            key={String(item.classId)}
            borderRadius="8px"
            sx={{
              "@media (hover: hover)": {
                "&:hover": {
                  boxShadow: theme.shadow.cardFeatured,
                },
              },
            }}
            onClick={() => item.slug && goToDetails(item.slug)}
          >
            <Flex justifyContent="space-between" alignItems="center" mb={2}>
              <Text variant="primary" fontWeight="bold" fontSize="md" pr={2}>
                {item.name}
              </Text>
              <Text
                as="button"
                fontSize="sm"
                display="flex"
                fontWeight="600"
                color={theme.accent.primary}
                onClick={(e) => {
                  e.stopPropagation();
                  if (item.slug) goToDetails(item.slug);
                }}
              >
                View&nbsp;more
              </Text>
            </Flex>

            {item.description && (
              <Text
                mb={3}
                noOfLines={3}
                fontSize="sm"
                variant="secondary"
              >
                {item.description}
              </Text>
            )}

            <Divider mb={3} borderColor={theme.borderGlass.default} />

            <Flex gap={1} fontSize="sm" direction="column">
              {item.classDays?.length > 0 ? (
                item.classDays.map((d) => (
                  <Flex
                    key={`${d.day}-${d.startTime}-${d.endTime}`}
                    w="100%"
                    justify="space-between"
                  >
                    <Text variant="primary" fontWeight="600">
                      {d.day}:
                    </Text>
                    <Text variant="secondary" textAlign="right">
                      {formatTimeTo12Hour(d.startTime)} –{" "}
                      {formatTimeTo12Hour(d.endTime)}
                    </Text>
                  </Flex>
                ))
              ) : (
                <Text variant="secondary">-</Text>
              )}

              <Divider my={2} borderColor={theme.borderGlass.default} />

              {item.classRoom?.name && (
                <Flex justify="space-between">
                  <Text variant="primary" fontWeight="600">
                    Classroom:
                  </Text>
                  <Text variant="secondary">{item.classRoom.name}</Text>
                </Flex>
              )}

              {item.location?.name && (
                <Flex justify="space-between">
                  <Text variant="primary" fontWeight="600">
                    Location:
                  </Text>
                  <Text variant="secondary">{item.location.name}</Text>
                </Flex>
              )}
            </Flex>
          </Card>
        ))}
      </Flex>
    </>
  );
};

